import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import ProductDetailModal from '../../components/Product/ProductDetailModal';
import StorageBoxChart from '../../components/Storage/StorageBoxChart';
import StorageBreadcrumb from '../../components/Storage/StorageBreadcrumb';
import StorageMap from '../../components/Storage/StorageMap';
import StorageNodeColumns from '../../components/Storage/StorageNodeColumns';
import StorageNodeContents from '../../components/Storage/StorageNodeContents';
import StorageNodeList from '../../components/Storage/StorageNodeList';
import StoragePlaceSearchModal from '../../components/Storage/StoragePlaceSearchModal';
import StorageReviewQueue from '../../components/Storage/StorageReviewQueue';
import StorageViewToggle, {
  resolveStorageView,
} from '../../components/Storage/StorageViewToggle';
import { useStoragePlaceContents } from '../../components/Storage/useStoragePlaceContents';
import {
  createStorageNode,
  deleteStorageNode,
  listStorageNodes,
  updateStorageNode,
} from '../../clients/storage_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  addStorageUnitWithLevels,
  applyBoxEdit,
  directLotCountsFromNodes,
  findNode,
  getChildren,
  getNodePath,
  getStorageNodeTypeLabel,
  isSlottedBox,
  mergeCreatedStorageNode,
  normalizeStorageNode,
  readSlotCount,
  removeStorageNode,
  updateStorageBox,
} from '../../utils/storageTree';

const LOG_CONTEXT = 'StorageExplorerPage';

/**
 * "shelf" / "shelves" or "rack" / "racks".
 *
 * @param {string|undefined} subtype
 * @param {number} count
 * @returns {string}
 */
function levelWord(subtype, count) {
  if (subtype === 'rack') return count === 1 ? 'rack' : 'racks';
  if (subtype === 'drawer') return count === 1 ? 'drawer' : 'drawers';
  return count === 1 ? 'shelf' : 'shelves';
}

/**
 * Toast text after an add. A slotted box names its slot count.
 *
 * @param {import('../../utils/storageTree').StorageNode} node
 * @param {Array<{ subtype?: string }>} levels
 * @param {number} [savedCount]
 * @returns {string}
 */
function describeAdded(node, levels, savedCount = levels.length) {
  const slots = readSlotCount(node);
  if (slots && !levels.length) {
    return `${node.name} added with ${slots} ${slots === 1 ? 'slot' : 'slots'}`;
  }
  return describeAddedUnit(node?.name, levels, savedCount);
}

function describeAddedUnit(unitName, levels, savedCount = levels.length) {
  if (!levels.length) return `${unitName} added`;
  const saved = levels.slice(0, savedCount);
  const shelves = saved.filter(level => level?.subtype === 'shelf').length;
  const drawers = saved.filter(level => level?.subtype === 'drawer').length;
  if (shelves && drawers && savedCount === levels.length) {
    return `${unitName} added with ${shelves} ${levelWord('shelf', shelves)} and ${drawers} ${levelWord('drawer', drawers)}`;
  }
  const subtype = levels[0]?.subtype;
  if (savedCount === levels.length) {
    return `${unitName} added with ${savedCount} ${levelWord(subtype, savedCount)}`;
  }
  return `${unitName} added with ${savedCount} of ${levels.length} ${levelWord(subtype, levels.length)}`;
}

/**
 * Visual storage map at `/storage` (lab only).
 *
 * The whole tree is one draggable flowchart: pan the background, wheel to zoom,
 * drag a card to rearrange. Add a room from the button on the map. Add anything
 * else from the form inside that place's card. Hierarchy (room → unit →
 * shelf/rack/drawer → box) is enforced by `storageTree`, not by the canvas.
 * A box marked slotted stores a slot count; clicking it opens that chart.
 *
 * **Map or List.** `?view=list` swaps the flowchart for `StorageNodeList`, the
 * same places as one collapsible tree with the same row actions. The two views
 * share this page's selection, handlers, breadcrumb, and modals; the page also
 * keeps the dragged card positions and which list rows are open, so switching
 * back and forth does not reset either view.
 *
 * `GET /storage-nodes/` loads the lab tree on mount. Card totals use `lot_count`
 * on each shelf and box. The lots inside a place are requested when that card
 * is hovered or clicked. When the tree loads, Add
 * POSTs and Remove DELETEs, using the server `id` (never a `local-*` id). A 404
 * means the API is not available, so the page keeps a session-only tree and a
 * warning. A 403 or 500 is an error: the empty map does not open. Dragged
 * positions are not persisted. The tree is never mirrored to localStorage.
 */
const StorageExplorerPage = () => {
  const { token, isSupplier } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = resolveStorageView(searchParams.get('view'));
  const [nodes, setNodes] = useState([]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [persisted, setPersisted] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Bumped when the breadcrumb jumps, so the map re-fits the view.
  const [fitSignal, setFitSignal] = useState(0);
  // Bumped when the list should open the path to the selected place and scroll to it.
  const [revealSignal, setRevealSignal] = useState(0);
  // Card positions and open list rows live here, not in the view that unmounts.
  const dragPositionsRef = useRef(new Map());
  const [listExpandedIds, setListExpandedIds] = useState(() => new Set());
  const [chartBoxId, setChartBoxId] = useState(null);
  const [inspectId, setInspectId] = useState(null);
  const [chartRefresh, setChartRefresh] = useState(0);
  const [reviewRefresh, setReviewRefresh] = useState(0);
  // Added to the tree's lot_count after a move or a place save this visit.
  const [lotCountDeltas, setLotCountDeltas] = useState({});
  const {
    previewPlace,
    cancelPreview,
    pinPlace,
    loadPlace,
    drop: dropPlaceContents,
  } = useStoragePlaceContents(token);
  const [placeRequest, setPlaceRequest] = useState(null);
  const [placeProduct, setPlaceProduct] = useState(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setLoading(false);
      return undefined;
    }
    listStorageNodes(token).then(result => {
      if (!active) return;
      setLoading(false);
      if (result.success) {
        const loaded = (result.data || []).map(normalizeStorageNode);
        setNodes(loaded);
        setPersisted(true);
        setLoadError(null);
        // The list starts with the rooms open, so the lab reads top down.
        const roomIds = getChildren(loaded, null).map(room => String(room.id));
        setListExpandedIds(new Set(roomIds));
        logInfo(LOG_CONTEXT, 'storage tree loaded', {
          nodeCount: loaded.length,
          roomCount: roomIds.length,
        });
        return;
      }
      if (result.notDeployed) {
        setPersisted(false);
        setLoadError(null);
        logInfo(LOG_CONTEXT, 'storage API unavailable, session-only tree', {
          status: result.status ?? null,
          notDeployed: true,
        });
        return;
      }
      const message = result.messages?.[0] || 'Could not load storage';
      setPersisted(false);
      setLoadError(message);
      logError(LOG_CONTEXT, 'storage tree load failed', {
        status: result.status ?? null,
      });
      showToast(message, 'error', 'top-right', 4000);
    });
    return () => {
      active = false;
    };
  }, [token]);

  const treeLotCounts = useMemo(() => directLotCountsFromNodes(nodes), [nodes]);
  const directLotCounts = useMemo(() => {
    if (!treeLotCounts) return null;
    const next = { ...treeLotCounts };
    Object.entries(lotCountDeltas).forEach(([id, delta]) => {
      if (!Number.isFinite(next[id])) return;
      next[id] = Math.max(0, next[id] + Number(delta));
    });
    return next;
  }, [treeLotCounts, lotCountDeltas]);

  const shiftLotCounts = useCallback(shifts => {
    const rows = (shifts || []).filter(row => row && row.id != null && row.id !== '' && row.delta);
    if (!rows.length) return;
    logInfo(LOG_CONTEXT, 'adjust lot counts', {
      shifts: rows.map(row => ({ id: row.id, delta: row.delta })),
    });
    setLotCountDeltas(prev => {
      const next = { ...prev };
      rows.forEach(row => {
        const key = String(row.id);
        next[key] = (Number(next[key]) || 0) + row.delta;
      });
      return next;
    });
  }, []);

  /**
   * A lot move updates the two places involved. It does not reload every shelf
   * and box. Their lot lists are dropped so the next open fetches them again.
   */
  const adjustLotCounts = useCallback(
    change => {
      const moved = change?.moved || [];
      if (!moved.length) return;
      const shifts = [];
      const touched = [];
      moved.forEach(lot => {
        const from = lot?.boxId ?? lot?.storage_node_id;
        if (from == null || from === '') return;
        shifts.push({ id: from, delta: -1 });
        touched.push(from);
      });
      if (change?.storageNodeId != null && change.storageNodeId !== '') {
        shifts.push({ id: change.storageNodeId, delta: moved.length });
        touched.push(change.storageNodeId);
      }
      dropPlaceContents(touched);
      shiftLotCounts(shifts);
    },
    [dropPlaceContents, shiftLotCounts]
  );

  const inspectIdRef = useRef(null);
  const chartBoxIdRef = useRef(null);
  inspectIdRef.current = inspectId;
  chartBoxIdRef.current = chartBoxId;

  const loadInspectedContents = useCallback(
    ({ reload } = {}) => {
      const node = findNode(nodesRef.current, inspectIdRef.current);
      if (!node) return Promise.resolve({ cancelled: true, items: [] });
      return loadPlace(nodesRef.current, node, { pin: true, reload });
    },
    [loadPlace]
  );

  const loadChartContents = useCallback(
    ({ reload } = {}) => {
      const node = findNode(nodesRef.current, chartBoxIdRef.current);
      if (!node) return Promise.resolve({ cancelled: true, items: [] });
      return loadPlace(nodesRef.current, node, { pin: true, reload });
    },
    [loadPlace]
  );

  const selected = useMemo(() => findNode(nodes, selectedId), [nodes, selectedId]);
  const path = useMemo(() => getNodePath(nodes, selectedId), [nodes, selectedId]);
  const chartBox = useMemo(() => {
    const node = findNode(nodes, chartBoxId);
    return isSlottedBox(node) ? node : null;
  }, [nodes, chartBoxId]);
  const inspectNode = useMemo(() => {
    const node = findNode(nodes, inspectId);
    if (!node || isSlottedBox(node)) return null;
    return node;
  }, [nodes, inspectId]);

  /**
   * Breadcrumb jumps select an ancestor, re-frame the map on it, and open the
   * path to it in the list.
   */
  const handleNavigate = useCallback(nodeId => {
    setSelectedId(nodeId);
    setFitSignal(n => n + 1);
    setRevealSignal(n => n + 1);
  }, []);

  /**
   * Map, list, or columns. The choice stays in the URL so a refresh keeps it.
   * Switching to the list reveals whatever place is selected elsewhere; the
   * columns show that path on their own, because they are built from it.
   */
  const handleViewChange = useCallback(
    next => {
      const wanted = resolveStorageView(next);
      logInfo(LOG_CONTEXT, 'storage view changed', { from: view, to: wanted });
      setSearchParams(
        previous => {
          const params = new URLSearchParams(previous);
          if (wanted === 'map') params.delete('view');
          else params.set('view', wanted);
          return params;
        },
        { replace: true }
      );
      if (wanted === 'list') setRevealSignal(n => n + 1);
    },
    [setSearchParams, view]
  );

  /** Open a place in the list, so what was just added inside it is on screen. */
  const openPlaceInList = useCallback(nodeId => {
    if (nodeId == null || nodeId === '') return;
    setListExpandedIds(previous => {
      const next = new Set(previous);
      next.add(String(nodeId));
      return next;
    });
  }, []);

  const closePlace = useCallback(() => {
    setPlaceRequest(null);
    setPlaceProduct(null);
  }, []);

  /**
   * Place item on a shelf, drawer, rack, or box. The slot is chosen on the
   * inventory modal only when the destination is a slotted box.
   */
  const handlePlaceItem = useCallback(place => {
    logInfo(LOG_CONTEXT, 'place item opened from map card', {
      nodeId: place?.id ?? null,
      kind: place?.kind ?? null,
      subtype: place?.subtype ?? null,
    });
    setPlaceProduct(null);
    setPlaceRequest({ box: place, lockedSlotIndex: null });
  }, []);

  /** Place into an empty chart cell. The slot is already that cell. */
  const handlePlaceInSlot = useCallback(
    slotIndex => {
      if (!chartBox) return;
      logInfo(LOG_CONTEXT, 'place item opened from chart cell', {
        boxId: chartBox.id,
        slotIndex,
      });
      setPlaceProduct(null);
      setPlaceRequest({ box: chartBox, lockedSlotIndex: slotIndex });
    },
    [chartBox]
  );

  const handlePlaced = useCallback(
    detail => {
      setChartRefresh(n => n + 1);
      setReviewRefresh(n => n + 1);
      const destination = detail?.boxId ?? detail?.storageNodeId ?? null;
      const source = detail?.fromId ?? null;
      const samePlace =
        source != null && source !== '' && String(source) === String(destination);
      if (detail?.placed !== false && destination != null && destination !== '' && !samePlace) {
        const shifts = [];
        if (source != null && source !== '') shifts.push({ id: source, delta: -1 });
        shifts.push({ id: destination, delta: 1 });
        shiftLotCounts(shifts);
      }
      dropPlaceContents([destination, source]);
      logInfo(LOG_CONTEXT, 'lot placed from storage map', {
        boxId: destination,
        fromId: source,
        slotIndex: detail?.slotIndex ?? null,
        placed: detail?.placed !== false,
      });
      setPlaceRequest(null);
      setPlaceProduct(null);
    },
    [dropPlaceContents, shiftLotCounts]
  );

  /**
   * Clicking any place shows what's inside. A slotted box opens its chart.
   * Every other place opens its children and the lots in its boxes, including
   * when nothing is there.
   */
  const handleOpenNode = useCallback(
    node => {
      if (!node) return;
      pinPlace(nodesRef.current, node);
      setSelectedId(node.id);
      if (isSlottedBox(node)) {
        setInspectId(null);
        setChartBoxId(node.id);
        logInfo(LOG_CONTEXT, 'slotted box chart opened', {
          id: node.id,
          slotCount: readSlotCount(node),
        });
        return;
      }
      setChartBoxId(null);
      setInspectId(node.id);
      logInfo(LOG_CONTEXT, 'place contents opened', {
        id: node.id,
        kind: node.kind,
      });
    },
    [pinPlace]
  );

  /** A slotted box opens its chart. The map click and the card button both use this. */
  const handleOpenBox = useCallback(
    node => {
      if (!isSlottedBox(node)) return;
      pinPlace(nodesRef.current, node);
      setInspectId(null);
      setChartBoxId(node.id);
      setSelectedId(node.id);
      logInfo(LOG_CONTEXT, 'slotted box chart opened', {
        id: node.id,
        slotCount: readSlotCount(node),
      });
    },
    [pinPlace]
  );

  const handlePreviewPlace = useCallback(
    node => {
      if (!persisted || !node) return;
      previewPlace(nodesRef.current, node);
    },
    [persisted, previewPlace]
  );

  const handleCancelPreview = useCallback(
    node => {
      cancelPreview(nodesRef.current, node);
    },
    [cancelPreview]
  );

  /**
   * Validate locally, then persist when the list loaded. A cooled unit or a
   * cabinet may include several shelves (racks for a dewar); those are extra
   * POSTs under the server id. Returns false when the map must stay unchanged
   * so the add form can stay open.
   */
  const handleAdd = useCallback(
    async (parent, { name, subtype, levelCount, shelfCount, drawerCount, slotted, slotCount }) => {
      const draft = addStorageUnitWithLevels({
        nodes,
        parent,
        name,
        subtype,
        levelCount,
        shelfCount,
        drawerCount,
        slotted,
        slotCount,
      });
      if (!draft.node) {
        showToast(draft.error || 'Could not add', 'warning', 'top-right', 3500);
        return false;
      }
      if (!persisted) {
        setNodes(draft.nodes);
        openPlaceInList(parent?.id);
        logInfo(LOG_CONTEXT, 'session-only node added', {
          kind: draft.node.kind,
          subtype: draft.node.subtype ?? null,
          levelCount: draft.levels.length,
          slotCount: readSlotCount(draft.node),
        });
        showToast(
          describeAdded(draft.node, draft.levels),
          'success',
          'top-right',
          2000
        );
        return true;
      }
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        const payload = {
          parent_id: draft.node.parent_id,
          kind: draft.node.kind,
          name: draft.node.name,
        };
        if (draft.node.subtype) payload.subtype = draft.node.subtype;
        if (draft.node.slot_count) payload.slot_count = draft.node.slot_count;
        const created = await createStorageNode(token, payload);
        if (!created.success) {
          const message = created.messages?.[0] || 'Could not add';
          logError(LOG_CONTEXT, 'create storage node rejected', {
            status: created.status ?? null,
          });
          showToast(message, 'warning', 'top-right', 4000);
          return false;
        }

        const createdNode = mergeCreatedStorageNode(created.data, draft.node);
        const saved = [createdNode];
        let savedLevels = 0;
        for (const level of draft.levels) {
          const levelPayload = {
            parent_id: created.data.id,
            kind: 'level',
            name: level.name,
            subtype: level.subtype,
          };
          const levelResult = await createStorageNode(token, levelPayload);
          if (!levelResult.success) {
            logError(LOG_CONTEXT, 'create bulk level rejected', {
              status: levelResult.status ?? null,
              unitId: created.data.id,
              savedLevels,
              requestedLevels: draft.levels.length,
            });
            setNodes(prev => [...prev, ...saved]);
            openPlaceInList(parent?.id);
            const partial = describeAddedUnit(
              created.data?.name || draft.node.name,
              draft.levels,
              savedLevels
            );
            const detail = levelResult.messages?.[0];
            showToast(detail ? `${partial}. ${detail}` : partial, 'warning', 'top-right', 4500);
            return true;
          }
          saved.push(levelResult.data);
          savedLevels += 1;
        }

        setNodes(prev => [...prev, ...saved]);
        openPlaceInList(parent?.id);
        logInfo(LOG_CONTEXT, 'storage node persisted', {
          id: createdNode?.id,
          kind: createdNode?.kind ?? draft.node.kind,
          levelCount: savedLevels,
          slotCount: readSlotCount(createdNode),
        });
        showToast(
          describeAdded(createdNode || draft.node, draft.levels, savedLevels),
          'success',
          'top-right',
          2000
        );
        return true;
      } finally {
        inFlightRef.current = false;
      }
    },
    [nodes, openPlaceInList, persisted, token]
  );

  /**
   * Edit a box already on the map. When the list is persisted, PATCH name and
   * slot_count. A 405 keeps the edit for this session. Returns false so the
   * form stays open when the map must not change.
   */
  const handleEditBox = useCallback(
    async (box, { name, slotted, slotCount }) => {
      const draft = updateStorageBox({ nodes, box, name, slotted, slotCount });
      if (!draft.node) {
        showToast(draft.error || 'Could not update', 'warning', 'top-right', 3500);
        return false;
      }
      if (!persisted) {
        setNodes(draft.nodes);
        logInfo(LOG_CONTEXT, 'session-only box updated', {
          id: draft.node.id,
          slotCount: readSlotCount(draft.node),
        });
        showToast(`${draft.node.name} updated`, 'success', 'top-right', 2000);
        return true;
      }
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        const payload = {
          name: draft.node.name,
          slot_count: readSlotCount(draft.node),
        };
        const updated = await updateStorageNode(token, box.id, payload);
        if (updated.methodMissing) {
          setNodes(draft.nodes);
          logInfo(LOG_CONTEXT, 'box edit kept for this session, PATCH is not available', {
            id: box.id,
            status: updated.status ?? null,
          });
          showToast(
            `${draft.node.name} updated on this map. A refresh drops the edit until the server accepts it.`,
            'warning',
            'top-right',
            4500
          );
          return true;
        }
        if (!updated.success) {
          const message = updated.messages?.[0] || 'Could not update';
          logError(LOG_CONTEXT, 'update storage box rejected', {
            status: updated.status ?? null,
            id: box.id,
          });
          showToast(message, 'warning', 'top-right', 4000);
          return false;
        }
        const saved = applyBoxEdit(updated.data, draft.node);
        setNodes(prev =>
          prev.map(node => (node && String(node.id) === String(box.id) ? saved : node))
        );
        logInfo(LOG_CONTEXT, 'storage box persisted', {
          id: saved.id,
          slotCount: readSlotCount(saved),
        });
        showToast(`${saved.name} updated`, 'success', 'top-right', 2000);
        return true;
      } finally {
        inFlightRef.current = false;
      }
    },
    [nodes, persisted, token]
  );

  /**
   * Move a place under a new parent. Children and lots stay on this node.
   * The tree updates only when the server echoes the new parent_id.
   */
  const handleMove = useCallback(
    async (node, parentId) => {
      if (!node || parentId == null || parentId === '') return false;
      if (!persisted) {
        setNodes(prev =>
          prev.map(item =>
            item && String(item.id) === String(node.id)
              ? { ...item, parent_id: parentId }
              : item
          )
        );
        logInfo(LOG_CONTEXT, 'session-only node moved', {
          id: node.id,
          parentId,
        });
        showToast(`${node.name} moved`, 'success', 'top-right', 2000);
        return true;
      }
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        const updated = await updateStorageNode(token, node.id, { parent_id: parentId });
        if (!updated.success) {
          const message = updated.messages?.[0] || 'Could not move';
          logError(LOG_CONTEXT, 'move storage node rejected', {
            id: node.id,
            parentId,
            status: updated.status ?? null,
          });
          showToast(message, 'warning', 'top-right', 4000);
          return false;
        }
        const echoed = updated.data?.parent_id;
        if (String(echoed) !== String(parentId)) {
          logError(LOG_CONTEXT, 'move storage node was not stored', {
            id: node.id,
            parentId,
            echoedParentId: echoed ?? null,
          });
          showToast(
            'That move was not stored. The place is still where it was.',
            'warning',
            'top-right',
            4000
          );
          return false;
        }
        const saved = {
          ...node,
          ...normalizeStorageNode(updated.data),
          parent_id: echoed,
        };
        setNodes(prev =>
          prev.map(item => (item && String(item.id) === String(node.id) ? saved : item))
        );
        logInfo(LOG_CONTEXT, 'storage node moved', {
          id: saved.id,
          parentId: echoed,
          kind: saved.kind,
        });
        showToast(`${saved.name || node.name} moved`, 'success', 'top-right', 2000);
        return true;
      } finally {
        inFlightRef.current = false;
      }
    },
    [persisted, token]
  );

  /**
   * Rename any place. Only the name is sent. The card updates when the response
   * echoes that name.
   */
  const handleRename = useCallback(
    async (node, name) => {
      const trimmed = String(name || '').trim();
      if (!node || !trimmed) return false;
      if (!persisted) {
        setNodes(prev =>
          prev.map(item =>
            item && String(item.id) === String(node.id) ? { ...item, name: trimmed } : item
          )
        );
        logInfo(LOG_CONTEXT, 'session-only node renamed', {
          id: node.id,
          kind: node.kind,
        });
        showToast(`${trimmed} renamed`, 'success', 'top-right', 2000);
        return true;
      }
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        const updated = await updateStorageNode(token, node.id, { name: trimmed });
        if (!updated.success) {
          const message = updated.messages?.[0] || 'Could not rename';
          logError(LOG_CONTEXT, 'rename storage node rejected', {
            id: node.id,
            status: updated.status ?? null,
          });
          showToast(message, 'warning', 'top-right', 4000);
          return false;
        }
        const echoed = String(updated.data?.name || '').trim();
        if (echoed !== trimmed) {
          logError(LOG_CONTEXT, 'rename storage node was not stored', {
            id: node.id,
            echoedName: echoed || null,
          });
          showToast(
            'That name was not stored. The place keeps its old name.',
            'warning',
            'top-right',
            4000
          );
          return false;
        }
        const saved = { ...node, ...normalizeStorageNode(updated.data), name: echoed };
        setNodes(prev =>
          prev.map(item => (item && String(item.id) === String(node.id) ? saved : item))
        );
        logInfo(LOG_CONTEXT, 'storage node renamed', {
          id: saved.id,
          kind: saved.kind,
        });
        showToast(`${echoed} renamed`, 'success', 'top-right', 2000);
        return true;
      } finally {
        inFlightRef.current = false;
      }
    },
    [persisted, token]
  );

  /**
   * Delete confirmed in the app dialog. Does not share the add/move lock, so a
   * save already running cannot swallow it.
   */
  const deleteStoragePlace = useCallback(
    async (authToken, nodeId) => {
      const placeName = String(findNode(nodesRef.current, nodeId)?.name || '').trim();
      const toastMessage = placeName ? `Removed ${placeName}` : 'Removed';
      if (!persisted) {
        logInfo(LOG_CONTEXT, 'session-only node removed', { id: nodeId, name: placeName || null });
        return {
          success: true,
          toast: () => showToast(toastMessage, 'success', 'top-right', 2000),
        };
      }
      const deleted = await deleteStorageNode(authToken, nodeId);
      if (!deleted.success) {
        logError(LOG_CONTEXT, 'delete storage node rejected', {
          status: deleted.status ?? null,
          id: nodeId,
        });
        return deleted.messages || ['Could not remove'];
      }
      logInfo(LOG_CONTEXT, 'storage node deleted', { id: nodeId, name: placeName || null });
      return {
        success: true,
        toast: () => showToast(toastMessage, 'success', 'top-right', 2000),
      };
    },
    [persisted]
  );

  const handleDeleted = useCallback(node => {
    if (!node) return;
    setNodes(prev => removeStorageNode(prev, node.id));
    setSelectedId(current =>
      current != null && String(current) === String(node.id) ? node.parent_id ?? null : current
    );
    dropPlaceContents([node.id]);
  }, [dropPlaceContents]);

  const showTree = !loading && !loadError;

  const body = (
    <>
      <div className="glass-page-heading">
        <h2 className="mb-0">Storage</h2>
        {selected ? (
          <span className="text-muted">{getStorageNodeTypeLabel(selected)}</span>
        ) : null}
        <div className="ms-auto">
          <StorageViewToggle view={view} onChange={handleViewChange} />
        </div>
      </div>
      <p className="glass-page-sub">
        {isViewer
          ? `This account is read-only. You can look through the lab's rooms,
             storage units, shelves, and boxes, and open a place to see the lots
             inside it.`
          : view === 'columns'
          ? `The lab room by room, left to right. Click a room to see its storage
             units beside it, then its shelves, then its boxes. Everything you can
             do to the place you picked is in the bar underneath, and Open shows
             what is inside it. A slotted box opens its chart.`
          : view === 'list'
            ? `The whole lab as a list. Open a place to see what is inside it, or
               search a room, freezer, drawer, or box. Every row does what its map
               card does. Click a place to see what's inside. A slotted box opens
               its chart.`
            : `The whole lab on one map. Drag the background to move around, scroll to
               zoom, and drag a card to rearrange it. Add a room from the button on the
               map, and add what goes inside a place from the button on its card.
               Click a place to see what's inside. A slotted box opens its chart.`}
      </p>

      {loadError && !loading ? (
        <Alert variant="danger" className="py-2 small">
          {loadError}
        </Alert>
      ) : null}

      {!persisted && !loading && !loadError ? (
        <Alert variant="warning" className="py-2 small">
          The storage API is not available yet, so this map is only kept while the
          page is open. Refreshing clears it, including where you dragged things.
        </Alert>
      ) : null}

      {showTree ? (
        <StorageBreadcrumb path={path} onNavigate={handleNavigate} />
      ) : null}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : showTree ? (
        <>
        {!isViewer ? (
        <StorageReviewQueue
          token={token}
          nodes={nodes}
          refreshSignal={reviewRefresh}
          onPlaced={handlePlaced}
        />
        ) : null}
        {view === 'columns' ? (
          <StorageNodeColumns
            nodes={nodes}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={isViewer ? undefined : handleAdd}
            onRemove={isViewer ? undefined : deleteStoragePlace}
            onDeleted={handleDeleted}
            onOpenNode={handleOpenNode}
            onOpenBox={handleOpenBox}
            onEditBox={isViewer ? undefined : handleEditBox}
            onMove={isViewer ? undefined : handleMove}
            onRename={isViewer ? undefined : handleRename}
            onPlaceItem={isViewer ? undefined : handlePlaceItem}
            onPreviewPlace={handlePreviewPlace}
            onCancelPreview={handleCancelPreview}
            lotCounts={directLotCounts}
          />
        ) : view === 'list' ? (
          <StorageNodeList
            nodes={nodes}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={isViewer ? undefined : handleAdd}
            onRemove={isViewer ? undefined : deleteStoragePlace}
            onDeleted={handleDeleted}
            onOpenNode={handleOpenNode}
            onOpenBox={handleOpenBox}
            onEditBox={isViewer ? undefined : handleEditBox}
            onMove={isViewer ? undefined : handleMove}
            onRename={isViewer ? undefined : handleRename}
            onPlaceItem={isViewer ? undefined : handlePlaceItem}
            onPreviewPlace={handlePreviewPlace}
            onCancelPreview={handleCancelPreview}
            lotCounts={directLotCounts}
            expandedIds={listExpandedIds}
            onExpandedChange={setListExpandedIds}
            revealSignal={revealSignal}
          />
        ) : (
          <StorageMap
            nodes={nodes}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={isViewer ? undefined : handleAdd}
            onRemove={isViewer ? undefined : deleteStoragePlace}
            onDeleted={handleDeleted}
            onOpenNode={handleOpenNode}
            onOpenBox={handleOpenBox}
            onEditBox={isViewer ? undefined : handleEditBox}
            onMove={isViewer ? undefined : handleMove}
            onRename={isViewer ? undefined : handleRename}
            onPlaceItem={isViewer ? undefined : handlePlaceItem}
            onPreviewPlace={handlePreviewPlace}
            onCancelPreview={handleCancelPreview}
            lotCounts={directLotCounts}
            fitSignal={fitSignal}
            dragPositionsRef={dragPositionsRef}
          />
        )}
        </>
      ) : null}
      <StorageNodeContents
        node={inspectNode}
        nodes={nodes}
        onClose={() => setInspectId(null)}
        onOpenNode={handleOpenNode}
        onPlaceItem={isViewer ? undefined : handlePlaceItem}
        onLotsChanged={adjustLotCounts}
        contentsLoader={loadInspectedContents}
      />
      <StorageBoxChart
        box={chartBox}
        nodes={nodes}
        onClose={() => setChartBoxId(null)}
        onPlaceInSlot={isViewer ? undefined : handlePlaceInSlot}
        refreshSignal={chartRefresh}
        onLotsChanged={adjustLotCounts}
        contentsLoader={loadChartContents}
      />
      <StoragePlaceSearchModal
        show={Boolean(placeRequest) && !placeProduct}
        box={placeRequest?.box}
        lockedSlotIndex={placeRequest?.lockedSlotIndex ?? null}
        onSelect={product => {
          logInfo(LOG_CONTEXT, 'product chosen to place', {
            productId: product?.id,
            boxId: placeRequest?.box?.id ?? null,
          });
          setPlaceProduct(product);
        }}
        onHide={closePlace}
      />
      {placeProduct && placeRequest ? (
        <ProductDetailModal
          isSupplier={Boolean(isSupplier)}
          productObj={placeProduct}
          productId={placeProduct.id}
          hideTrigger
          show
          onHide={closePlace}
          placeTarget={
            isViewer
              ? null
              : {
                  box: placeRequest.box,
                  lockedSlotIndex: placeRequest.lockedSlotIndex,
                  onPlaced: handlePlaced,
                }
          }
        />
      ) : null}
    </>
  );

  return (
    <GlassWorkspaceShell theme="neutral" wide>
      {body}
    </GlassWorkspaceShell>
  );
};

export default StorageExplorerPage;
